/* Perspective hand cutaway. World geometry and catalogue placements are in mm. */
(function (global) {
  'use strict';
  const NS = 'http://www.w3.org/2000/svg';
  const NAMES = ['Thumb', 'Index', 'Middle', 'Ring', 'Little'];
  const INK = '#173c53', BLUE = '#029ccb', RETURN = '#9366ab';
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const num = value => Number.isFinite(value) ? Number(value.toFixed(4)) : 0;
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
  /* Put the thumb drive beside the thumb so its tendons do not cross the palm. */
  const lane = index => index===0 ? 2 : index-3;
  const project = q => [q[0] + .22*q[1] - .15*q[2], .75*q[1] + .85*q[2]];
  const add = (a,b) => a.map((v,i) => v+b[i]);
  const sub = (a,b) => a.map((v,i) => v-b[i]);
  const mul = (a,k) => a.map(v => v*k);
  const points = list => list.map(q => project(q).map(num).join(',')).join(' ');
  const poly = (list, fill, attrs='') => `<polygon points="${points(list)}" fill="${fill}" stroke="${INK}" stroke-width=".65" stroke-linejoin="round" ${attrs}/>`;
  const line = (list, color, width=.65, attrs='') => `<polyline points="${points(list)}" fill="none" stroke="${color}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round" ${attrs}/>`;
  function extents(list) {
    return [Math.min(...list.map(q=>q[0])),Math.min(...list.map(q=>q[1])),Math.max(...list.map(q=>q[0])),Math.max(...list.map(q=>q[1]))];
  }
  const boxCorners = b => [[b[0],b[1]],[b[0],b[3]],[b[2],b[1]],[b[2],b[3]]];
  function driveEnvelope(entry) {
    const e=entry||{},closed=Math.max(1,Number(e.closedMm)||50),open=Math.max(closed,Number(e.openMm)||95);
    const valid=b=>Array.isArray(b)&&b.length===4&&b.every(Number.isFinite);
    const view=e.viewBox;
    const body=valid(e.bodyBounds)?e.bodyBounds:valid(e.rollBounds)?e.rollBounds:
      valid(view)?[view[0],view[1],view[0]+view[2],view[1]+view[3]]:[-8,-8,closed,8];
    const pinY=Number(e.frontPinCad?.[1])||0;
    /* placeBetween rotates CAD by -asin(frontPinY / desired pin length). Bound
       that whole closed-to-open rotation interval, not just the current pose. */
    const theta0=-Math.asin(clamp(pinY/closed,-1,1)),theta1=-Math.asin(clamp(pinY/open,-1,1));
    const lo=Math.min(theta0,theta1),hi=Math.max(theta0,theta1),bodyPoints=[];
    for(const [x,y] of boxCorners(body)) {
      const angles=[lo,hi];
      for(const origin of [Math.atan2(-y,x),Math.atan2(x,y)])for(let k=-2;k<=2;k++) {
        const angle=origin+k*Math.PI;if(angle>lo&&angle<hi)angles.push(angle);
      }
      for(const angle of angles)bodyPoints.push([x*Math.cos(angle)-y*Math.sin(angle),x*Math.sin(angle)+y*Math.cos(angle)]);
    }
    /* The catalogue radius covers the shaft/front-end envelope. Retain a pin
       and stroke-end margin as well; neither the end eye nor rod can be clipped. */
    const radius=Math.max(4,Number(e.rodRadiusMm)||0,(Number(e.pinDiameterMm)||0)*1.5)+2;
    const rodPoints=boxCorners([0,-radius,open+radius,radius]);
    return {points:[...bodyPoints,...rodPoints],bounds:extents([...bodyPoints,...rodPoints])};
  }
  function text(x,y,label,size=10,attrs='') {
    return `<text x="${num(x)}" y="${num(y)}" font-family="Arial, sans-serif" font-size="${size}" fill="${INK}" ${attrs}>${esc(label)}</text>`;
  }
  function box(x0,x1,y0,y1,z0,z1,fill='#e7eff4') {
    return poly([[x0,y0,z0],[x1,y0,z0],[x1,y1,z0],[x0,y1,z0]], '#c8d8e3')
      +poly([[x0,y1,z0],[x1,y1,z0],[x1,y1,z1],[x0,y1,z1]], '#90a7b7')
      +poly([[x1,y0,z0],[x1,y1,z0],[x1,y1,z1],[x1,y0,z1]], '#bacbd6')
      +poly([[x0,y0,z1],[x1,y0,z1],[x1,y1,z1],[x0,y1,z1]], fill);
  }
  function disc(center, u, v, radius, fill, attrs='') {
    return poly(Array.from({length:24}, (_,i) => add(center,add(mul(u,radius*Math.cos(i*Math.PI/12)),mul(v,radius*Math.sin(i*Math.PI/12))))),fill,attrs);
  }
  function sideBasis(index) {
    const yaw = ([40,-6,-1,4,10][index])*Math.PI/180;
    return [Math.cos(yaw),Math.sin(yaw),0];
  }
  function fingerMap(index, xy, palmEnd, pitch, z=0) {
    const [x,curl] = xy;
    if (index===0) {
      const c=Math.cos(40*Math.PI/180),s=Math.sin(40*Math.PI/180);
      return [palmEnd-34+c*x+.6*s*curl,pitch*1.55+s*x-.6*c*curl,8+.8*curl+z];
    }
    const sideY = (index-2.5)*pitch*.76;
    const basis=sideBasis(index);
    return [palmEnd+basis[0]*x,sideY+basis[1]*x,8+curl+z];
  }
  function offsetJoints(joints, distance) {
    return joints.map((q,i) => {
      const before=joints[Math.max(0,i-1)],after=joints[Math.min(joints.length-1,i+1)];
      const dx=after[0]-before[0],dy=after[1]-before[1],length=Math.hypot(dx,dy)||1;
      return [q[0]-dy/length*distance,q[1]+dx/length*distance];
    });
  }
  function phalanx(a,b,side,width,thickness,active) {
    const d=sub(b,a),length=Math.hypot(...d)||1,tangent=mul(d,1/length);
    const normal=[tangent[1]*side[2]-tangent[2]*side[1],tangent[2]*side[0]-tangent[0]*side[2],tangent[0]*side[1]-tangent[1]*side[0]];
    let nlen=Math.hypot(...normal);
    /* The hinge axis lies across the digit; its normal is in the flexion plane. */
    const normalSafe=nlen>1e-6?mul(normal,1/nlen):[0,0,1];
    const corners=(q) => [add(q,add(mul(side,-width/2),mul(normalSafe,-thickness/2))),add(q,add(mul(side,width/2),mul(normalSafe,-thickness/2))),add(q,add(mul(side,width/2),mul(normalSafe,thickness/2))),add(q,add(mul(side,-width/2),mul(normalSafe,thickness/2)))];
    const aa=corners(add(a,mul(tangent,2.5))),bb=corners(add(b,mul(tangent,-2.5)));
    return poly([aa[0],bb[0],bb[1],aa[1]],'#9aadb9')
      +poly([aa[1],bb[1],bb[2],aa[2]],'#b2c4d1')
      +poly([aa[2],bb[2],bb[3],aa[3]],active?'#9fd9ef':'#e5d9c1')
      +poly([bb[0],bb[1],bb[2],bb[3]],'#dce8f0');
  }
  function fingerArt(index,pose,palmEnd,pitch,active,radiusMm,ghost=false) {
    const joints=pose.joints||[],world=joints.map(q=>fingerMap(index,q,palmEnd,pitch));
    if(world.length<2)return '';
    const basis=sideBasis(index),axis=index===0?[-.8*basis[1],.8*basis[0],.6]:[-basis[1],basis[0],0],width=index===0?14:12;
    if(ghost)return `<g data-unreachable-finger="${index}" opacity=".5">${line(world,'#de7e22',1.15,'stroke-dasharray="3 2"')}</g>`;
    let art=`<g data-finger="${index}" data-finger-name="${NAMES[index]}" data-travel-mm="${num(pose.travelMm)}" data-joint-angles="${esc(JSON.stringify(pose.jointAnglesDeg||[]))}">`;
    for(let j=0;j<world.length-1;j++) art+=phalanx(world[j],world[j+1],axis,width-j*1.6,7,active);
    for(let j=0;j<world.length;j++) {
      const center=add(world[j],mul(axis,width/2+.3));
      const circleU=basis,circleV=index===0?[.6*basis[1],-.6*basis[0],.8]:[0,0,1];
      art+=disc(center,circleU,circleV,j===world.length-1?3.3:4.7,'#5d798d',`data-joint="${j}" data-local-x-mm="${num(joints[j][0])}" data-local-curl-mm="${num(joints[j][1])}"`)
        +disc(center,circleU,circleV,j===world.length-1?1.2:2.2,'#eaf6fc');
    }
    const flexor=offsetJoints(joints,radiusMm).map(q=>fingerMap(index,q,palmEnd,pitch));
    const extensor=offsetJoints(joints,-radiusMm).map(q=>fingerMap(index,q,palmEnd,pitch));
    art+=line(extensor,RETURN,.85,'stroke-dasharray="2 1.4" data-tendon="return"')+line(flexor,BLUE,1.05,'data-tendon="flexor"');
    /* Matching digits and numbered actuators are selectable by the parent UI. */
    const badge=project(fingerMap(index,joints[0],palmEnd,pitch,15));
    art+=`<circle cx="${num(badge[0])}" cy="${num(badge[1])}" r="5" fill="${active?BLUE:'#fff'}" stroke="${INK}" stroke-width=".7"/>`
      +text(badge[0],badge[1]+2.1,index+1,6.1,`text-anchor="middle" style="fill:${active?'#fff':INK};font-weight:bold"`);
    return art+'</g>';
  }
  function draw(host,p,poses,drives,active=0) {
    if(!host||!Array.isArray(poses)||poses.length!==5)return;
    const model=global.FGRobotHandModel;
    const cadEnvelopes=drives.map(d=>driveEnvelope(d?.entry));
    const lower=Math.min(...cadEnvelopes.map(e=>e.bounds[1])),upper=Math.max(...cadEnvelopes.map(e=>e.bounds[3]));
    /* Mixed families may have motors on opposite sides. A maximum width alone
       does not pack those asymmetric envelopes without overlap. */
    const pitch=Math.max(23,upper-lower+7);
    const maxOpen=Math.max(95,...drives.map(d=>Number(d?.entry?.openMm)||0));
    const base=-maxOpen-24,palmEnd=82,halfWidth=pitch*2+16;
    const cadWorld=cadEnvelopes.map((e,i)=>e.points.map(q=>[base+q[0],lane(i)*pitch+q[1],0]));
    const frameMin=Math.min(base-20,...cadWorld.flat().map(q=>q[0]-8));
    const frameMax=Math.max(5,...cadWorld.flat().map(q=>q[0]+8));
    const frameYMin=Math.min(-halfWidth,...cadWorld.flat().map(q=>q[1]-8));
    const frameYMax=Math.max(halfWidth,...cadWorld.flat().map(q=>q[1]+8));
    const display=poses.map((pose,i)=>{
      const d=drives[i],entry=d?.entry,needed=Math.max(0,Number(d?.strokeNeededMm)||0);
      const available=entry?Math.max(0,Math.min(Number(entry.strokeMm)||0,(Number(entry.openMm)||0)-(Number(entry.closedMm)||0))):needed;
      const requested=Math.max(0,Number(pose.travelMm)||0),actualRemaining=clamp(needed-requested,0,available),actualTravel=needed-actualRemaining;
      const limited=actualTravel>requested+1e-6;
      return {pose:limited&&model?.pose?model.pose(p,i,needed?actualTravel/needed:0):pose,requested:pose,needed,available,limited,travel:actualTravel,length:(Number(entry?.closedMm)||50)+actualRemaining};
    });
    const allBounds=[];
    for(const x of [frameMin-7,frameMax])for(const y of [frameYMin-11,frameYMax+11])for(const z of [-9,10])allBounds.push(project([x,y,z]));
    /* These are the same base/lane translation and x+.22*y, .75*y transform
       applied to the actual CAD slots below. Include every body/rod corner. */
    for(const corners of cadWorld)allBounds.push(...corners.map(project));
    /* Bound the complete digit workspace, not only the current animation frame. */
    for(let i=0;i<5;i++) {
      const length=Number(p.fingers?.[i]?.lengthMm)||Math.max(1,...poses[i].joints.map(q=>Math.hypot(...q)));
      for(const x of [-length,length])for(const z of [-length*.1,length])allBounds.push(project(fingerMap(i,[x,z],palmEnd,pitch,15)));
    }
    const lowX=Math.min(...allBounds.map(q=>q[0])),highX=Math.max(...allBounds.map(q=>q[0]));
    const lowY=Math.min(...allBounds.map(q=>q[1])),highY=Math.max(...allBounds.map(q=>q[1]));
    const scale=Math.min(846/(highX-lowX),354/(highY-lowY));
    const tx=27-lowX*scale+(846-(highX-lowX)*scale)/2,ty=42-lowY*scale+(354-(highY-lowY)*scale)/2;
    let art='';
    /* Open forearm cradle. The blue-grey floor remains under all five drives. */
    art+=box(frameMin,frameMax,frameYMin,frameYMax,-8,-5,'#e0eaf0');
    art+=box(frameMin,frameMax,frameYMin,frameYMin+5,-5,7,'#e8d4b3');
    art+=box(frameMin,frameMin+6,frameYMin,frameYMax,-5,6,'#d8e5ef');
    art+=box(-3,5,frameYMin,frameYMax,-5,5,'#d8e5ef');
    art+=box(5,20,-halfWidth*.76,halfWidth*.76,-7,6,'#94aebe');
    /* Anatomical palm silhouette: narrower wrist, rounded knuckle end, thumb side flare. */
    const palm=[[18,-halfWidth*.73,-4],[palmEnd-6,-pitch*1.75,-4],[palmEnd+7,-pitch*1.27,-4],[palmEnd+7,pitch*1.32,-4],[palmEnd-20,pitch*2.04,-4],[20,halfWidth*.75,-4]];
    art+=poly(palm,'#a4bac9')+poly(palm.map(q=>[q[0],q[1],6]),'#f1e2c6','data-part="palm-cover"');
    art+=line([[26,-pitch,7],[palmEnd-11,-pitch,7],[palmEnd-4,0,7],[palmEnd-13,pitch,7],[26,pitch,7]],'#b8aa95',.65);
    /* Stationary return idlers and two independent tendon runs per drive. */
    const cables=[];
    for(let i=0;i<5;i++) {
      const y=lane(i)*pitch,rod=base+display[i].length,heel=base-10;
      const flexRoot=fingerMap(i,offsetJoints(display[i].pose.joints,p.radiusMm)[0],palmEnd,pitch);
      const returnRoot=fingerMap(i,offsetJoints(display[i].pose.joints,-p.radiusMm)[0],palmEnd,pitch);
      const wristFlex=[13,y*.65,9],wristReturn=[10,y*.65+3,5];
      art+=box(base-3,base+4,y-6,y+6,-4,2,'#809cac');
      art+=disc([heel,y+9,1],[1,0,0],[0,1,0],3.3,'#e3eef5',`data-heel-idler="${i}"`)
        +disc([heel,y+9,1],[1,0,0],[0,1,0],1,'#426176');
      /* The return strand wraps around its heel idler before heading to the wrist. */
      const back=[[rod,y+5,4],[heel,y+5,4],[heel-3.6,y+6.3,4],[heel-4.1,y+9,4],[heel-2.8,y+12.2,4],[heel,y+13,4],[1,y+13,4],wristReturn,returnRoot];
      const front=[[rod,y,4],[1,y,8],wristFlex,flexRoot];
      cables.push(line(back,RETURN,i===active?1:.7,`stroke-dasharray="2 1.6" data-drive-return="${i}"`)
        +line(front,BLUE,i===active?1.2:.8,`data-drive-flexor="${i}"`));
      const badge=project([base-10,y-3,5]);
      art+=`<circle cx="${num(badge[0])}" cy="${num(badge[1])}" r="4.5" fill="${i===active?BLUE:'#fff'}" stroke="${INK}" stroke-width=".6"/>`
        +text(badge[0],badge[1]+1.9,i+1,5.7,`text-anchor="middle" style="fill:${i===active?'#fff':INK};font-weight:bold"`);
      const screenBounds=extents(cadWorld[i].map(q=>{const projected=project(q);return [tx+scale*projected[0],ty+scale*projected[1]];}));
      art+=`<g data-cad-slot="${i}" data-drive="${i}" data-drive-length-mm="${num(display[i].length)}" data-drive-travel-mm="${num(display[i].travel)}" data-stroke-limited="${display[i].limited}" data-cad-screen-bounds="${esc(JSON.stringify(screenBounds.map(num)))}" transform="matrix(1 0 .22 .75 0 0)"></g>`;
      art+=line([[rod,y-3,2],[rod,y+5,2]],'#54788d',1.6,`data-crosshead="${i}"`);
    }
    art+=cables.join('');
    /* The near wall is cut below the drive plane so it cannot hide actuator 1. */
    art+=box(frameMin,frameMax,frameYMax-5,frameYMax,-5,-1,'#e4d1af');
    /* Selected digit is a foreground cutaway layer: it stays visible when curled
       behind unselected straight fingers. This changes paint order, not pose. */
    for(const i of [...[1,2,3,4,0].filter(i=>i!==active),active]) {
      if(display[i].limited&&i===active)art+=fingerArt(i,display[i].requested,palmEnd,pitch,false,p.radiusMm,true);
      art+=fingerArt(i,display[i].pose,palmEnd,pitch,i===active,p.radiusMm);
    }
    const armLabel=project([(frameMin+frameMax)/2,frameYMin,0]);
    const palmLabel=project([palmEnd-5,pitch*2.06,0]);
    const labelX=clamp(tx+scale*armLabel[0],150,420),label2X=clamp(tx+scale*palmLabel[0],560,765);
    host.innerHTML=`<svg xmlns="${NS}" viewBox="0 0 900 450" role="img" aria-label="Mechanical robot hand and cutaway forearm: five independent actuators pull paired tendons to curl four fingers and an opposing thumb. Highlighted digit ${esc(NAMES[active])}." data-view="perspective-hand" data-frame-length-mm="${num(frameMax-frameMin)}" data-frame-bounds-mm="${esc(JSON.stringify([frameMin,frameYMin,frameMax,frameYMax].map(num)))}" data-art-bounds="${esc(JSON.stringify([lowX,lowY,highX,highY].map(num)))}" data-render-transform="${esc(JSON.stringify([scale,tx,ty].map(num)))}" data-selected-finger="${active}">
      <rect x="1" y="1" width="898" height="448" fill="transparent"/>
      <g transform="translate(${num(tx)} ${num(ty)}) scale(${num(scale)})">${art}</g>
      ${text(labelX,21,'5 independent forearm drives',11,'text-anchor="middle" font-weight="bold"')}
      ${text(labelX,35,`${num(frameMax-frameMin)} mm illustrated forearm package`,9,'text-anchor="middle"')}
      ${text(label2X,414,'Blue digit = selected finger cutaway',10,'text-anchor="middle"')}
      <line x1="28" y1="420" x2="50" y2="420" stroke="${BLUE}" stroke-width="2"/>${text(57,424,'Flexor',10)}
      <line x1="113" y1="420" x2="135" y2="420" stroke="${RETURN}" stroke-width="2" stroke-dasharray="4 3"/>${text(142,424,'Return via fixed heel idler',10)}
      ${text(28,442,'Perspective cutaway · tendon routes are schematic; finger joints follow the coupled model.',9)}
      </svg>`;
    drives.forEach((d,i)=>{
      const slot=host.querySelector(`[data-cad-slot="${i}"]`);
      if(!slot)return;
      if(d?.renderer&&d?.entry) {
        try {
          d.renderer.placeBetween([base,lane(i)*pitch],[base+display[i].length,lane(i)*pitch]);
          slot.appendChild(d.renderer.element);
          slot.setAttribute('data-actuator-id',d.entry.id||'');
        } catch(error) {
          slot.setAttribute('data-drawing-error',String(error.message||error));
          slot.innerHTML=`<text x="${num(base+12)}" y="${num(lane(i)*pitch+2)}" font-size="6" fill="#a74024">CAD unavailable</text>`;
        }
      } else {
        slot.innerHTML=`<text x="${num(base+12)}" y="${num(lane(i)*pitch+2)}" font-size="6" fill="#658396">Loading actuator ${i+1}…</text>`;
      }
    });
  }
  global.FGRobotHandDrawing={draw};
})(typeof window==='undefined'?globalThis:window);
